import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { apiClient } from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, AlertCircle, PlusCircle, Edit, Trash, RefreshCw, CheckCircle, X, Clock, Calendar, Sparkles, Eye } from 'lucide-react';
import DataTable from '../components/common/DataTable';
import InfoModal from '../components/common/InfoModal';
import CustomDatePicker from '../components/common/CustomDatePicker';
import { SpecialDayBannerCard, BANNER_VISUAL_MOTIFS, resolveBannerMotif } from '../components/dashboard/DashboardSpecialDayBanner';

const parseLocalDate = (val) => {
    if (!val) return null;
    if (val instanceof Date) {
        return new Date(val.getFullYear(), val.getMonth(), val.getDate());
    }
    const str = String(val).split('T')[0];
    const parts = str.split('-');
    if (parts.length === 3) {
        return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    }
    const d = new Date(val);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const toLocalDateString = (date) => {
    const d = parseLocalDate(date);
    if (!d || isNaN(d.getTime())) return null;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const toLocalISOString = (date) => {
    const str = toLocalDateString(date);
    return str ? `${str}T00:00:00` : null;
};

const MOTIF_SHORT_LABELS = {
    ataturk: 'Atatürk & Ay-Yıldız',
    flag: 'Türk Bayrağı',
    religious: 'Hilal & Kandil (Ramazan)',
    kurban: 'Kâbe & Hilal (Kurban)',
    celebration: 'Kutlama & Balon',
    newyear: 'Yılbaşı & Kar',
    labor: '1 Mayıs Dayanışma',
    company: 'Kurumsal Rozet',
    info: 'Bilgilendirme'
};

const PublicHolidaysPage = () => {
    const { firmCode, isAuthenticated, permissions } = useAuth();

    const [holidays, setHolidays] = useState([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);

    const [newHolidayDate, setNewHolidayDate] = useState(null);
    const [newHolidayEndDate, setNewHolidayEndDate] = useState(null);
    const [newHolidayDescription, setNewHolidayDescription] = useState('');
    const [isHalfDay, setIsHalfDay] = useState(false);
    const [calculationMethod, setCalculationMethod] = useState('FixedTime');
    const [halfDayStartTime, setHalfDayStartTime] = useState('13:00');
    const [proportionalPercentage, setProportionalPercentage] = useState(50);
    const [breakDeductionPolicy, setBreakDeductionPolicy] = useState('DeductOnlyOverlapping');

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState(false);
    const [selectedHolidayForEdit, setSelectedHolidayForEdit] = useState(null);
    const [holidayToDeleteId, setHolidayToDeleteId] = useState(null);

    // Banner Settings States
    const [showAsBanner, setShowAsBanner] = useState(false);
    const [bannerSettings, setBannerSettings] = useState({
        title: '',
        message: '',
        themeType: 1,
        iconKey: '',
        themeColor: '',
        isDismissible: true,
        priority: 8
    });

    const THEME_TYPES = [
        { value: 1, label: 'Milli / Resmi Bayram (Al Bayrak Kırmızı)' },
        { value: 2, label: 'Dini Bayram (Zümrüt Yeşili & Altın)' },
        { value: 3, label: 'Şirket Özel (Kraliyet Mavisi)' },
        { value: 4, label: 'Kutlama / Yeni Yıl (Fuşya / Mor)' },
        { value: 5, label: 'Bilgilendirme (Slate / Cyan)' }
    ];

    const HOLIDAY_SOURCE = useMemo(
        () => ({
            SYSTEM: 'System',
            USER: 'User'
        }),
        []
    );

    const normalizeCalcMethod = (val) => {
        if (val === 1 || val === 'Proportional') return 'Proportional';
        return 'FixedTime';
    };

    const normalizeBreakPolicy = (val) => {
        if (val === 1 || val === 'DoNotDeduct') return 'DoNotDeduct';
        if (val === 2 || val === 'DeductOnlyOverlapping') return 'DeductOnlyOverlapping';
        return 'DeductNormally';
    };

    const isSystemSource = useCallback(
        (source) => {
            return source === HOLIDAY_SOURCE.SYSTEM || source === 0;
        },
        [HOLIDAY_SOURCE]
    );

    const canView = useMemo(() => permissions.includes('public_holidays:view_list'), [permissions]);
    const canCreate = useMemo(() => permissions.includes('public_holidays:create'), [permissions]);
    const canUpdate = useMemo(() => permissions.includes('public_holidays:update'), [permissions]);
    const canDelete = useMemo(() => permissions.includes('public_holidays:delete'), [permissions]);

    const fetchHolidays = useCallback(async () => {
        if (!canView) {
            setError('Resmi tatilleri listeleme yetkiniz bulunmamaktadır.');
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            const data = await apiClient('PublicHoliday');
            setHolidays(data || []);
        } catch (err) {
            setError(err.message || 'Resmi tatiller yüklenirken bir hata oluştu.');
            console.error('API Hatası:', err);
        } finally {
            setIsLoading(false);
        }
    }, [canView]);

    useEffect(() => {
        if (!isAuthenticated || !firmCode) {
            setError('Giriş yapılmamış veya firma kodu mevcut değil.');
            setIsLoading(false);
            return;
        }
        if (!isAddModalOpen && !isEditModalOpen && !isDeleteConfirmModalOpen) {
            fetchHolidays();
        }
    }, [firmCode, isAuthenticated, isAddModalOpen, isEditModalOpen, isDeleteConfirmModalOpen, fetchHolidays]);

    // Mevcut yıllar ve filtrelenmiş liste
    const availableYears = useMemo(() => {
        const yearsSet = new Set([new Date().getFullYear(), new Date().getFullYear() + 1]);
        holidays.forEach((h) => {
            const d = parseLocalDate(h.date);
            if (d) yearsSet.add(d.getFullYear());
        });
        return Array.from(yearsSet).sort((a, b) => a - b);
    }, [holidays]);

    const filteredHolidays = useMemo(() => {
        if (selectedYear === 'ALL') return holidays;
        return holidays.filter((h) => {
            const d = parseLocalDate(h.date);
            return d && d.getFullYear() === Number(selectedYear);
        });
    }, [holidays, selectedYear]);

    const stats = useMemo(() => {
        const list = filteredHolidays;
        const fullDays = list.filter((h) => !h.isHalfDay).length;
        const halfDays = list.filter((h) => h.isHalfDay).length;
        const activeBanners = list.filter((h) => h.bannerSettings && h.bannerSettings.showOnDashboard !== false).length;
        return {
            total: list.length,
            fullDays,
            halfDays,
            activeBanners
        };
    }, [filteredHolidays]);

    const handleSyncHolidays = async () => {
        setIsSyncing(true);
        setError(null);
        setMessage(null);
        try {
            const res = await apiClient('PublicHoliday/sync', { method: 'POST' });
            setMessage(res?.message || 'Resmi tatiller ve arife (yarım gün) kayıtları senkronize edildi.');
            await fetchHolidays();
        } catch (err) {
            setError(err.message || 'Resmi tatiller senkronize edilirken hata oluştu.');
        } finally {
            setIsSyncing(false);
        }
    };

    const calculateDurationDays = useCallback((startDate, endDate) => {
        const start = parseLocalDate(startDate);
        const end = parseLocalDate(endDate);

        if (!start) return 0;
        if (!end) return 1;

        const diffTime = Math.abs(end.getTime() - start.getTime());
        const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;
        return diffDays;
    }, []);

    const clearAddForm = () => {
        setNewHolidayDate(null);
        setNewHolidayEndDate(null);
        setNewHolidayDescription('');
        setIsHalfDay(false);
        setCalculationMethod('FixedTime');
        setHalfDayStartTime('13:00');
        setProportionalPercentage(50);
        setBreakDeductionPolicy('DeductOnlyOverlapping');

        setShowAsBanner(false);
        setBannerSettings({
            title: '',
            message: '',
            themeType: 1,
            iconKey: '',
            themeColor: '',
            isDismissible: true,
            priority: 8
        });
        setError(null);
    };

    const handleAddClick = () => {
        clearAddForm();
        setMessage(null);
        setIsAddModalOpen(true);
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();

        if (!canCreate) {
            setError('Yeni resmi tatil oluşturma yetkiniz bulunmamaktadır.');
            return;
        }

        setError(null);
        setMessage(null);

        if (!newHolidayDate || !newHolidayDescription.trim()) {
            setError('Başlangıç tarihi ve açıklama boş bırakılamaz.');
            return;
        }
        if (!newHolidayEndDate) {
            setError('Bitiş tarihi boş bırakılamaz.');
            return;
        }
        if (newHolidayEndDate < newHolidayDate) {
            setError('Bitiş tarihi, başlangıç tarihinden önce olamaz.');
            return;
        }

        setIsSubmitting(true);
        const formattedDate = toLocalISOString(newHolidayDate);
        const formattedEndDate = toLocalISOString(newHolidayEndDate);

        try {
            await apiClient('PublicHoliday', {
                method: 'POST',
                body: {
                    date: formattedDate,
                    endDate: formattedEndDate,
                    description: newHolidayDescription.trim(),
                    isHalfDay,
                    calculationMethod: isHalfDay ? calculationMethod : 'FixedTime',
                    halfDayStartTime: isHalfDay && calculationMethod === 'FixedTime' ? halfDayStartTime + ':00' : null,
                    proportionalPercentage: isHalfDay && calculationMethod === 'Proportional' ? proportionalPercentage : null,
                    breakDeductionPolicy: isHalfDay ? breakDeductionPolicy : 'DeductNormally',
                    bannerSettings: showAsBanner
                        ? {
                              ...bannerSettings,
                              showOnDashboard: true
                          }
                        : null
                }
            });
            setMessage('Tatil başarıyla eklendi.');
            fetchHolidays();
            handleAddModalClose();
        } catch (err) {
            setError(err.message || 'Tatil eklenirken bir hata oluştu.');
            console.error('API Hatası:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEditClick = (holiday) => {
        setSelectedHolidayForEdit({
            ...holiday,
            date: parseLocalDate(holiday.date),
            endDate: parseLocalDate(holiday.endDate || holiday.date)
        });
        setIsHalfDay(Boolean(holiday.isHalfDay));
        setCalculationMethod(normalizeCalcMethod(holiday.calculationMethod));
        setHalfDayStartTime(holiday.halfDayStartTime ? String(holiday.halfDayStartTime).substring(0, 5) : '13:00');
        setProportionalPercentage(holiday.proportionalPercentage || 50);
        setBreakDeductionPolicy(normalizeBreakPolicy(holiday.breakDeductionPolicy));

        const desc = holiday.description || '';
        const isRel = desc.toLocaleLowerCase('tr-TR').includes('ramazan') || desc.toLocaleLowerCase('tr-TR').includes('kurban');

        if (holiday.bannerSettings) {
            setShowAsBanner(holiday.bannerSettings.showOnDashboard !== false);
            setBannerSettings({
                title: holiday.bannerSettings.title || desc,
                message: holiday.bannerSettings.message || `${desc} kutlu olsun.`,
                themeType: holiday.bannerSettings.themeType || (isRel ? 2 : 1),
                iconKey: holiday.bannerSettings.iconKey || '',
                themeColor: holiday.bannerSettings.themeColor === 'blue' ? '' : (holiday.bannerSettings.themeColor || ''),
                isDismissible: holiday.bannerSettings.isDismissible !== undefined ? holiday.bannerSettings.isDismissible : true,
                priority: holiday.bannerSettings.priority ?? 8
            });
        } else {
            setShowAsBanner(false);
            setBannerSettings({
                title: desc,
                message: `${desc} kutlu olsun.`,
                themeType: isRel ? 2 : 1,
                iconKey: '',
                themeColor: '',
                isDismissible: true,
                priority: 8
            });
        }
        setIsEditModalOpen(true);
        setError(null);
    };

    const handleEditSubmit = async (updatedHoliday) => {
        if (!canUpdate) {
            setError('Resmi tatil güncelleme yetkiniz bulunmamaktadır.');
            return;
        }

        if (!updatedHoliday.date || !updatedHoliday.description.trim()) {
            setError('Başlangıç tarihi ve açıklama boş bırakılamaz.');
            return;
        }
        if (!updatedHoliday.endDate) {
            setError('Bitiş tarihi boş bırakılamaz.');
            return;
        }
        if (updatedHoliday.endDate < updatedHoliday.date) {
            setError('Bitiş tarihi, başlangıç tarihinden önce olamaz.');
            return;
        }

        setIsSubmitting(true);
        setError(null);
        setMessage(null);

        const formattedDate = toLocalISOString(updatedHoliday.date);
        const formattedEndDate = toLocalISOString(updatedHoliday.endDate);

        try {
            await apiClient(`PublicHoliday/${updatedHoliday.id}`, {
                method: 'PUT',
                body: {
                    id: updatedHoliday.id,
                    date: formattedDate,
                    endDate: formattedEndDate,
                    description: updatedHoliday.description.trim(),
                    isHalfDay,
                    calculationMethod: isHalfDay ? calculationMethod : 'FixedTime',
                    halfDayStartTime: isHalfDay && calculationMethod === 'FixedTime' ? halfDayStartTime + ':00' : null,
                    proportionalPercentage: isHalfDay && calculationMethod === 'Proportional' ? proportionalPercentage : null,
                    breakDeductionPolicy: isHalfDay ? breakDeductionPolicy : 'DeductNormally',
                    bannerSettings: showAsBanner
                        ? {
                              ...bannerSettings,
                              showOnDashboard: true
                          }
                        : null
                }
            });
            setMessage('Tatil ve yarım gün kuralları başarıyla güncellendi.');
            fetchHolidays();
            handleEditModalClose();
        } catch (err) {
            setError(err.message || 'Tatil güncellenirken bir hata oluştu.');
            console.error('API Hatası:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteRequest = (id) => {
        setHolidayToDeleteId(id);
        setIsDeleteConfirmModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!canDelete) {
            setError('Resmi tatil silme yetkiniz bulunmamaktadır.');
            setIsDeleteConfirmModalOpen(false);
            return;
        }

        if (!holidayToDeleteId) return;

        setIsSubmitting(true);
        setError(null);
        setMessage(null);
        try {
            await apiClient(`PublicHoliday/${holidayToDeleteId}`, {
                method: 'DELETE'
            });
            setMessage('Tatil başarıyla silindi.');
            setIsDeleteConfirmModalOpen(false);
            fetchHolidays();
        } catch (err) {
            setError(err.message || 'Tatil silinirken bir hata oluştu.');
            console.error('API Hatası:', err);
        } finally {
            setIsSubmitting(false);
            setHolidayToDeleteId(null);
        }
    };

    const handleAddModalClose = () => {
        setIsAddModalOpen(false);
        clearAddForm();
    };
    const handleEditModalClose = () => {
        setIsEditModalOpen(false);
        setSelectedHolidayForEdit(null);
        setError(null);
    };
    const handleDeleteModalClose = () => {
        setIsDeleteConfirmModalOpen(false);
        setHolidayToDeleteId(null);
    };

    const holidayColumns = useMemo(
        () => [
            {
                header: 'Tarih Aralığı',
                accessor: 'dateRange',
                sortable: false,
                searchable: true,
                render: (row) => {
                    const startDate = parseLocalDate(row.date);
                    const endDate = parseLocalDate(row.endDate);
                    if (!startDate) return '-';
                    const dateString = startDate.toLocaleDateString('tr-TR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        weekday: 'short'
                    });
                    const endDateString =
                        endDate && startDate.toDateString() !== endDate.toDateString()
                            ? ` - ${endDate.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })}`
                            : '';
                    return <span className="font-medium text-gray-900 dark:text-gray-100">{`${dateString}${endDateString}`}</span>;
                }
            },
            { header: 'Tatil Açıklaması', accessor: 'description', sortable: true, searchable: true },
            {
                header: 'Tatil Tipi & Kuralı',
                accessor: 'isHalfDay',
                render: (row) => {
                    if (!row.isHalfDay) {
                        return (
                            <span className="px-2.5 py-0.5 inline-flex text-xs leading-5 font-medium rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                                Tam Gün Resmi Tatil
                            </span>
                        );
                    }
                    const method = normalizeCalcMethod(row.calculationMethod);
                    const ruleText =
                        method === 'Proportional'
                            ? `Oransal (%${row.proportionalPercentage || 50} Tatil)`
                            : `Sabit Saat (${row.halfDayStartTime ? String(row.halfDayStartTime).substring(0, 5) : '13:00'} sonrası tatil)`;
                    return (
                        <div className="flex flex-col space-y-0.5">
                            <span className="px-2.5 py-0.5 inline-flex items-center w-fit text-xs leading-5 font-semibold rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                                <Clock className="w-3.5 h-3.5 mr-1" /> Yarım Gün (Arife)
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{ruleText}</span>
                        </div>
                    );
                }
            },
            {
                header: 'Dashboard Kutlama Banner\'ı',
                accessor: 'bannerSettings',
                render: (row) => {
                    const b = row.bannerSettings;
                    if (!b || b.showOnDashboard === false) {
                        return (
                            <span className="px-2 py-0.5 inline-flex text-xs leading-5 rounded-full bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                                Gösterilmiyor
                            </span>
                        );
                    }
                    const motif = resolveBannerMotif(b);
                    const isReligious = Number(b.themeType) === 2 || motif === 'religious';
                    return (
                        <div className="flex flex-col space-y-0.5">
                            <span
                                className={`px-2.5 py-0.5 inline-flex items-center w-fit text-xs leading-5 font-semibold rounded-full ${
                                    isReligious
                                        ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300'
                                        : 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300'
                                }`}
                            >
                                <Sparkles className="w-3 h-3 mr-1" />
                                {MOTIF_SHORT_LABELS[motif] || 'Aktif Banner'}
                            </span>
                            <span className="text-[11px] text-gray-500 dark:text-gray-400 truncate max-w-[200px]" title={b.title}>
                                {b.title}
                            </span>
                        </div>
                    );
                }
            },
            {
                header: 'Kaynak',
                accessor: 'source',
                render: (row) =>
                    isSystemSource(row.source) ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                            Sistem
                        </span>
                    ) : (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300">
                            Kullanıcı
                        </span>
                    )
            },
            {
                header: 'İşlemler',
                accessor: 'actions',
                render: (row) => (
                    <div className="flex justify-end space-x-2">
                        {canUpdate && (
                            <button
                                onClick={() => handleEditClick(row)}
                                className="p-1.5 rounded-full text-indigo-500 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-gray-700"
                                title="Düzenle / Yarım Gün ve Kutlama Görseli Ayarla"
                            >
                                <Edit className="w-5 h-5" />
                            </button>
                        )}
                        {canDelete && (
                            <button
                                onClick={() => handleDeleteRequest(row.id)}
                                className="p-1.5 rounded-full text-red-500 hover:bg-red-50 dark:text-red-400 dark:hover:bg-gray-700"
                                title="Sil"
                            >
                                <Trash className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                )
            }
        ],
        [canUpdate, canDelete, isSystemSource]
    );

    const renderHalfDaySection = (checkboxId) => (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40 p-4">
            <div className="flex items-center justify-between">
                <label htmlFor={checkboxId} className="flex items-center cursor-pointer">
                    <input
                        type="checkbox"
                        id={checkboxId}
                        checked={isHalfDay}
                        onChange={(e) => setIsHalfDay(e.target.checked)}
                        className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                    />
                    <span className="ml-2.5 text-sm font-semibold text-gray-800 dark:text-gray-200">
                        Yarım Gün Resmi Tatil (Arife Günü Kuralı)
                    </span>
                </label>
                <span
                    className={`px-2 py-0.5 text-[11px] font-semibold rounded-full ${
                        isHalfDay
                            ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                            : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                    }`}
                >
                    {isHalfDay ? 'Yarım Gün' : 'Tam Gün'}
                </span>
            </div>

            {isHalfDay ? (
                <div className="mt-3 pt-3 border-t border-amber-200/70 dark:border-gray-700 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="sm:col-span-2">
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Hesaplama Yöntemi</label>
                            <select
                                value={calculationMethod}
                                onChange={(e) => setCalculationMethod(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            >
                                <option value="FixedTime">Sabit Saate Göre (Örn: 13:00 sonrası)</option>
                                <option value="Proportional">Vardiya Oranına Göre (Örn: %50)</option>
                            </select>
                        </div>
                        {calculationMethod === 'FixedTime' ? (
                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Başlangıç Saati</label>
                                <input
                                    type="time"
                                    value={halfDayStartTime}
                                    onChange={(e) => setHalfDayStartTime(e.target.value)}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                    required={isHalfDay && calculationMethod === 'FixedTime'}
                                />
                            </div>
                        ) : (
                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Tatil Oranı (%)</label>
                                <input
                                    type="number"
                                    min="1"
                                    max="100"
                                    value={proportionalPercentage}
                                    onChange={(e) => setProportionalPercentage(parseInt(e.target.value, 10) || 50)}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                    required={isHalfDay && calculationMethod === 'Proportional'}
                                />
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Mola Düşme Kuralı</label>
                        <select
                            value={breakDeductionPolicy}
                            onChange={(e) => setBreakDeductionPolicy(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                            <option value="DeductOnlyOverlapping">Sadece Tatil Başlangıç Saatinden Önceki Molaları Düş</option>
                            <option value="DeductNormally">Normal Vardiya Molalarını Tam Düş</option>
                            <option value="DoNotDeduct">Molaları Düşme (Çalışma Süresine Dahil Et)</option>
                        </select>
                    </div>
                </div>
            ) : (
                <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                    Tam gün resmi tatil olarak uygulanır. Arife günü gibi öğleden sonra başlayan tatiller için kutucuğu işaretleyin.
                </p>
            )}
        </div>
    );

    const renderBannerSettingsFields = () => (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40 p-4 h-full flex flex-col">
            <div className="flex items-center justify-between">
                <label htmlFor="showAsBannerToggle" className="flex items-center cursor-pointer">
                    <input
                        type="checkbox"
                        id="showAsBannerToggle"
                        checked={showAsBanner}
                        onChange={(e) => setShowAsBanner(e.target.checked)}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <span className="ml-2.5 text-sm font-semibold text-gray-800 dark:text-gray-200">
                        Dashboard'da Kutlama Teması / Banner Göster
                    </span>
                </label>
                <span
                    className={`px-2 py-0.5 text-[11px] font-semibold rounded-full ${
                        showAsBanner
                            ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300'
                            : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    }`}
                >
                    {showAsBanner ? 'Aktif' : 'Kapalı'}
                </span>
            </div>

            {showAsBanner ? (
                <div className="mt-3 pt-3 border-t border-indigo-200/70 dark:border-gray-700 space-y-3">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Banner Başlığı *</label>
                        <input
                            type="text"
                            value={bannerSettings.title}
                            onChange={(e) => setBannerSettings({ ...bannerSettings, title: e.target.value })}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            placeholder="Örn: 29 Ekim Cumhuriyet Bayramımız Kutlu Olsun!"
                            required={showAsBanner}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Kutlama Mesajı *</label>
                        <textarea
                            value={bannerSettings.message}
                            onChange={(e) => setBannerSettings({ ...bannerSettings, message: e.target.value })}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            rows="2"
                            required={showAsBanner}
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Tema Rengi / Tipi</label>
                            <select
                                value={bannerSettings.themeType}
                                onChange={(e) => setBannerSettings({ ...bannerSettings, themeType: parseInt(e.target.value, 10) })}
                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            >
                                {THEME_TYPES.map((t) => (
                                    <option key={t.value} value={t.value}>
                                        {t.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Görsel Motif / Siluet</label>
                            <select
                                value={bannerSettings.iconKey || ''}
                                onChange={(e) => setBannerSettings({ ...bannerSettings, iconKey: e.target.value })}
                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            >
                                {BANNER_VISUAL_MOTIFS.map((m) => (
                                    <option key={m.value} value={m.value}>
                                        {m.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center pt-1">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Öncelik (Priority)</label>
                            <input
                                type="number"
                                value={bannerSettings.priority}
                                onChange={(e) => setBannerSettings({ ...bannerSettings, priority: parseInt(e.target.value, 10) || 0 })}
                                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                min="0"
                            />
                        </div>
                        <div className="flex items-center sm:pt-5">
                            <input
                                type="checkbox"
                                id="bannerDismissibleToggle"
                                checked={bannerSettings.isDismissible}
                                onChange={(e) => setBannerSettings({ ...bannerSettings, isDismissible: e.target.checked })}
                                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                            />
                            <label htmlFor="bannerDismissibleToggle" className="ml-2 block text-xs font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                                Kullanıcı kapatabilsin mi?
                            </label>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex-1 flex flex-col justify-center text-xs text-gray-500 dark:text-gray-400">
                    <p>
                        Bu tatil gününde Ana Sayfa (Dashboard) üzerinde kutlama görseli gösterilmez.
                        Atatürk silueti, Ay-Yıldız, dini bayram kandilleri veya kutlama banner'ı göstermek için kutucuğu işaretleyin.
                    </p>
                </div>
            )}
        </div>
    );

    if (!canView && !isLoading) {
        return <div className="p-4 text-red-700">Bu sayfayı görüntüleme yetkiniz bulunmamaktadır.</div>;
    }

    return (
        <div className="p-1 space-y-4">
            {message && (
                <div className="bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 p-3 rounded-md flex items-center animate-fade-in-down">
                    <CheckCircle className="w-5 h-5 mr-2" /> {message}
                </div>
            )}
            {error && (
                <div className="bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 p-3 rounded-md flex items-center animate-fade-in-down">
                    <AlertCircle className="w-5 h-5 mr-2" /> {error}
                </div>
            )}

            {/* Üst Yıl Filtresi ve Özet Kartlar */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 bg-white dark:bg-gray-800 p-3.5 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-2 overflow-x-auto">
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mr-1 flex items-center">
                        <Calendar className="w-4 h-4 mr-1 text-indigo-500" /> Yıl Filtresi:
                    </span>
                    {availableYears.map((yr) => (
                        <button
                            key={yr}
                            type="button"
                            onClick={() => setSelectedYear(yr)}
                            className={`px-3.5 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                                Number(selectedYear) === yr
                                    ? 'bg-indigo-600 text-white shadow-sm'
                                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
                            }`}
                        >
                            {yr}
                        </button>
                    ))}
                    <button
                        type="button"
                        onClick={() => setSelectedYear('ALL')}
                        className={`px-3.5 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                            selectedYear === 'ALL'
                                ? 'bg-indigo-600 text-white shadow-sm'
                                : 'bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
                        }`}
                    >
                        Tümü ({holidays.length})
                    </button>
                </div>

                <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 font-medium">
                        Tam Gün: <strong>{stats.fullDays}</strong>
                    </span>
                    <span className="px-2.5 py-1 rounded-md bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 font-medium">
                        Yarım Gün (Arife): <strong>{stats.halfDays}</strong>
                    </span>
                    <span className="px-2.5 py-1 rounded-md bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300 font-medium">
                        Aktif Kutlama Banner'ı: <strong>{stats.activeBanners}</strong>
                    </span>
                </div>
            </div>

            <DataTable
                data={filteredHolidays}
                columns={holidayColumns}
                itemsPerPage={20}
                showSearch={true}
                showPagination={true}
                emptyMessage="Seçili yılda henüz resmi tatil kaydı bulunmamaktadır."
                isLoading={isLoading}
                errorMessage={null}
                actionsHeader={
                    <div className="flex items-center space-x-3">
                        {(canCreate || canUpdate) && (
                            <button
                                onClick={handleSyncHolidays}
                                disabled={isSyncing}
                                className="flex items-center px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50"
                                title="Bu yıl ve gelecek yılın resmi tatillerini, Diyanet dini bayramlarını ve arife (yarım gün) kayıtlarını otomatik senkronize eder"
                            >
                                <RefreshCw className={`w-4 h-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
                                {isSyncing ? 'Senkronize Ediliyor...' : 'Resmi Tatil & Arifeleri Senkronize Et'}
                            </button>
                        )}
                        {canCreate && (
                            <button
                                onClick={handleAddClick}
                                className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md transition-colors"
                            >
                                <PlusCircle className="w-5 h-5 mr-2" />
                                Tatil Ekle
                            </button>
                        )}
                    </div>
                }
            />

            {/* Yeni Tatil Ekleme Modalı */}
            <InfoModal
                isOpen={isAddModalOpen}
                onClose={handleAddModalClose}
                title="Yeni Resmi Tatil Ekle"
                showFooter={false}
                className="max-w-5xl"
            >
                <form onSubmit={handleAddSubmit} className="space-y-4">
                    {error && (
                        <div className="bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 p-3 rounded-md flex items-center animate-fade-in-down">
                            <AlertCircle className="w-5 h-5 mr-2" /> {error}
                        </div>
                    )}

                    {/* Üst Canlı Banner Önizlemesi (Aktifse tam genişlikte görünür) */}
                    {showAsBanner && (
                        <div className="rounded-xl border border-indigo-200 dark:border-indigo-800/60 bg-indigo-50/40 dark:bg-gray-800/60 p-3">
                            <div className="flex items-center text-xs font-semibold text-indigo-700 dark:text-indigo-300 mb-1">
                                <Eye className="w-3.5 h-3.5 mr-1.5" />
                                Dashboard Kutlama Görseli Canlı Önizlemesi
                            </div>
                            <SpecialDayBannerCard banner={bannerSettings} isPreview={true} />
                        </div>
                    )}

                    {/* Yan Yana 2 Sütun: Sol = Tatil & Arife Kuralı, Sağ = Kutlama Banner Ayarları */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch">
                        {/* Sol Sütun */}
                        <div className="space-y-4 flex flex-col justify-between">
                            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40 p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">Tatil Tarih ve Bilgileri</span>
                                    {newHolidayDate && (
                                        <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                                            Toplam {calculateDurationDays(newHolidayDate, newHolidayEndDate)} Gün
                                        </span>
                                    )}
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Başlangıç Tarihi *</label>
                                        <CustomDatePicker
                                            selectedDate={toLocalDateString(newHolidayDate)}
                                            onDateChange={(dateString) => {
                                                const date = parseLocalDate(dateString);
                                                setNewHolidayDate(date);
                                                if (newHolidayEndDate && date && date > newHolidayEndDate) {
                                                    setNewHolidayEndDate(date);
                                                } else if (!newHolidayEndDate) {
                                                    setNewHolidayEndDate(date);
                                                }
                                            }}
                                            placeholder="GG.AA.YYYY"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Bitiş Tarihi *</label>
                                        <CustomDatePicker
                                            selectedDate={toLocalDateString(newHolidayEndDate)}
                                            onDateChange={(dateString) => setNewHolidayEndDate(parseLocalDate(dateString))}
                                            placeholder="GG.AA.YYYY"
                                            minDate={newHolidayDate}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="holidayDescription" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Tatil Açıklaması *
                                    </label>
                                    <input
                                        type="text"
                                        id="holidayDescription"
                                        value={newHolidayDescription}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setNewHolidayDescription(val);
                                            if (!bannerSettings.title) {
                                                setBannerSettings((prev) => ({
                                                    ...prev,
                                                    title: val,
                                                    message: `${val} kutlu olsun!`
                                                }));
                                            }
                                        }}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                        placeholder="Örn: 29 Ekim Cumhuriyet Bayramı"
                                        required
                                    />
                                </div>
                            </div>

                            {renderHalfDaySection('isHalfDay')}
                        </div>

                        {/* Sağ Sütun */}
                        <div>{renderBannerSettingsFields()}</div>
                    </div>

                    <div className="flex justify-end space-x-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <button
                            type="button"
                            onClick={handleAddModalClose}
                            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 flex items-center text-sm font-medium"
                            disabled={isSubmitting}
                        >
                            <X className="w-4 h-4 mr-1.5" /> İptal
                        </button>
                        <button
                            type="submit"
                            className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center disabled:opacity-50 text-sm font-semibold shadow-sm"
                            disabled={isSubmitting}
                        >
                            {isSubmitting && <Loader2 className="animate-spin mr-2" size={16} />}
                            Ekle
                        </button>
                    </div>
                </form>
            </InfoModal>

            {/* Düzenleme Modalı */}
            <InfoModal
                isOpen={isEditModalOpen}
                onClose={handleEditModalClose}
                title="Tatili, Yarım Gün Kuralını ve Kutlama Görselini Düzenle"
                showFooter={false}
                className="max-w-5xl"
            >
                {selectedHolidayForEdit && (
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleEditSubmit(selectedHolidayForEdit);
                        }}
                        className="space-y-4"
                    >
                        {error && (
                            <div className="bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 p-3 rounded-md flex items-center animate-fade-in-down">
                                <AlertCircle className="w-5 h-5 mr-2" /> {error}
                            </div>
                        )}

                        {/* Üst Canlı Banner Önizlemesi */}
                        {showAsBanner && (
                            <div className="rounded-xl border border-indigo-200 dark:border-indigo-800/60 bg-indigo-50/40 dark:bg-gray-800/60 p-3">
                                <div className="flex items-center text-xs font-semibold text-indigo-700 dark:text-indigo-300 mb-1">
                                    <Eye className="w-3.5 h-3.5 mr-1.5" />
                                    Dashboard Kutlama Görseli Canlı Önizlemesi
                                </div>
                                <SpecialDayBannerCard banner={bannerSettings} isPreview={true} />
                            </div>
                        )}

                        {/* Yan Yana 2 Sütun: Sol = Tatil & Arife Kuralı, Sağ = Kutlama Banner Ayarları */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch">
                            {/* Sol Sütun */}
                            <div className="space-y-4 flex flex-col justify-between">
                                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40 p-4 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">Tatil Tarih ve Bilgileri</span>
                                        {selectedHolidayForEdit.date && (
                                            <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                                                Toplam {calculateDurationDays(selectedHolidayForEdit.date, selectedHolidayForEdit.endDate)} Gün
                                            </span>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Başlangıç Tarihi *</label>
                                            <CustomDatePicker
                                                selectedDate={toLocalDateString(selectedHolidayForEdit.date)}
                                                onDateChange={(dateString) => {
                                                    const date = parseLocalDate(dateString);
                                                    setSelectedHolidayForEdit((prev) => ({
                                                        ...prev,
                                                        date: date,
                                                        endDate: prev.endDate && date && date > prev.endDate ? date : prev.endDate
                                                    }));
                                                }}
                                                placeholder="GG.AA.YYYY"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Bitiş Tarihi *</label>
                                            <CustomDatePicker
                                                selectedDate={toLocalDateString(selectedHolidayForEdit.endDate)}
                                                onDateChange={(dateString) =>
                                                    setSelectedHolidayForEdit((prev) => ({ ...prev, endDate: parseLocalDate(dateString) }))
                                                }
                                                placeholder="GG.AA.YYYY"
                                                minDate={selectedHolidayForEdit.date}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label htmlFor="editDescription" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Tatil Açıklaması *
                                        </label>
                                        <input
                                            type="text"
                                            id="editDescription"
                                            value={selectedHolidayForEdit.description}
                                            onChange={(e) => setSelectedHolidayForEdit((prev) => ({ ...prev, description: e.target.value }))}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                            placeholder="Tatil açıklaması"
                                            required
                                        />
                                    </div>
                                </div>

                                {renderHalfDaySection('editIsHalfDay')}
                            </div>

                            {/* Sağ Sütun */}
                            <div>{renderBannerSettingsFields()}</div>
                        </div>

                        <div className="flex justify-end space-x-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                            <button
                                type="button"
                                onClick={handleEditModalClose}
                                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 flex items-center text-sm font-medium"
                                disabled={isSubmitting}
                            >
                                <X className="w-4 h-4 mr-1.5" /> İptal
                            </button>
                            <button
                                type="submit"
                                className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center disabled:opacity-50 text-sm font-semibold shadow-sm"
                                disabled={isSubmitting}
                            >
                                {isSubmitting && <Loader2 className="animate-spin mr-2" size={16} />}
                                Kaydet
                            </button>
                        </div>
                    </form>
                )}
            </InfoModal>

            <InfoModal
                isOpen={isDeleteConfirmModalOpen}
                onClose={handleDeleteModalClose}
                title="Silme Onayı"
                message="Bu resmi tatili silmek istediğinizden emin misiniz? Bu işlem geri alınamaz."
                type="confirm"
                onConfirm={handleDeleteConfirm}
                confirmText="Evet, Sil"
                cancelText="İptal"
                isLoading={isSubmitting}
            />
        </div>
    );
};

export default PublicHolidaysPage;
